/**
 * /influencers/platforms — multi-platform influencer attribution page.
 *
 * Declares a TanStack Router route and renders the breadcrumb header plus the
 * `InfluencerPlatformsPage` component. The route auto-registers against the
 * scaffolded `rootRoute` — the root wire-up cycle picks it up by importing
 * from this module.
 */

import { createRoute, Link } from "@tanstack/react-router";
import { rootRoute } from "./__root";
import { InfluencerPlatformsPage } from "@/features/influencer-platforms/InfluencerPlatformsPage";

// ---------------------------------------------------------------------------
// Route — mounts at `/influencers/platforms`.
// `validateSearch` reserves room for a `?influencerId=` deep-link so operators
// can open the page straight from an influencer detail view with the id
// prefilled into the active-influencer context.
// ---------------------------------------------------------------------------

interface InfluencerPlatformsSearch {
	influencerId?: string;
}

export const influencersPlatformsRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/influencers/platforms",
	validateSearch: (raw: Record<string, unknown>): InfluencerPlatformsSearch => {
		const candidate = raw.influencerId ?? raw.influencer_id;
		if (typeof candidate === "string" && candidate.trim().length > 0) {
			return { influencerId: candidate.trim() };
		}
		return {};
	},
	component: InfluencersPlatformsRoute,
});

export default influencersPlatformsRoute;

// ---------------------------------------------------------------------------
// Page wrapper — breadcrumb + shell around the feature component. Static,
// light-theme, zinc borders, 6-8px rounded, no emojis, no competitor names.
// ---------------------------------------------------------------------------

export function InfluencersPlatformsRoute() {
	const { influencerId } = influencersPlatformsRoute.useSearch();

	return (
		<div className="flex flex-col gap-6 px-6 pt-10 sm:pt-14">
			<div className="mx-auto w-full max-w-5xl">
				<Breadcrumb />
			</div>
			<InfluencerPlatformsPage initialInfluencerId={influencerId} />
		</div>
	);
}

function Breadcrumb() {
	return (
		<nav aria-label="Breadcrumb" className="text-sm text-zinc-500">
			<ol className="flex flex-wrap items-center gap-1">
				<li>
					<Link
						to="/"
						className="rounded-[6px] px-1 py-0.5 hover:bg-zinc-100 hover:text-zinc-900"
					>
						Dashboard
					</Link>
				</li>
				<li aria-hidden="true" className="text-zinc-300">
					/
				</li>
				<li>
					<span className="rounded-[6px] px-1 py-0.5">Influencers</span>
				</li>
				<li aria-hidden="true" className="text-zinc-300">
					/
				</li>
				<li className="text-zinc-900">Platforms</li>
			</ol>
		</nav>
	);
}
